import React from "react";
import intl from "react-intl-universal";
import {ModelSortDirection} from "../../API";

import {ColId, ColSchema, Row} from "../../ui/components/Table";
import {TableDataSource} from "../dataSources/TableDataSource";
import {FilterSchema} from "../dataSources/DataSource";
import {BasicDataSourceDeps} from "../../platform/services/types/DataSourceDependencies";
import {GQLResponse} from "../gqlModel/types";
import {queryGqlApi} from "../../graphql/queryGqlApi";
import {listLocations} from "../../graphql/reliability/queries";
import {onCreateLocation, onUpdateLocation, onDeleteLocation} from "../../graphql/reliability/subscriptions";
import {LocationRowActions} from "../../ui/screens/Locations/LocationRowActions";

import {Location} from "./types";

const ENDPOINT = "pgServer";

const colOrder: ColId[] = ['name', 'address', 'actions'];

const colSchema: ColSchema = {
    name: {
        getTitle: () => intl.get('locations.tableCols.name').d('Name'),
        alignLeft: true,
        minWidth: 340,
    },
    address: {
        getTitle: () => intl.get('locations.tableCols.address').d('Address'),
        alignLeft: true,
        minWidth: 340,
    },
    actions: {
        getTitle: () => '',
        minWidth: 56,
        maxWidth: 56,
        alignLeft: false,
    },
};

/**
 * Workspace locations catalog — pure query + subscription layer.
 * Mutations live in LocationGqlProvider; user-facing flows in src/actions/location/.
 * Replaces the old LocationsStore + LocationsDataSource wrapper pair.
 */
export class LocationDS extends TableDataSource<Location> {
    readonly _modelName = 'Location' as const;
    readonly wsId: string;

    readonly _subscriptionGQLs = {
        onCreate: onCreateLocation,
        onUpdate: onUpdateLocation,
        onDelete: onDeleteLocation,
    };

    constructor(deps: BasicDataSourceDeps, wsId: string) {
        super(deps);
        this.wsId = wsId;
        this.setSortRule('name', ModelSortDirection.ASC);
    }

    get syncHash() { return this.wsId; }
    get syncQueryFilter() { return {workspaceId: this.wsId}; }

    async loadGQLFetch(): Promise<GQLResponse<Location>> {
        return queryGqlApi(
            listLocations,
            {workspaceId: this.wsId, includeArchived: false},
            this.deps.dataClientService,
            this.deps.authService,
            ENDPOINT,
        ) as Promise<GQLResponse<Location>>;
    }

    async loadMoreGQLFetch(nextToken: string): Promise<GQLResponse<Location>> {
        return queryGqlApi(
            listLocations,
            {workspaceId: this.wsId, includeArchived: false, nextToken},
            this.deps.dataClientService,
            this.deps.authService,
            ENDPOINT,
        ) as Promise<GQLResponse<Location>>;
    }

    getWsClient() {
        return this.deps.dataClientService.getWsClientByKey(ENDPOINT);
    }

    getColsSchema(): ColSchema { return colSchema; }
    getColOrder(): ColId[] { return colOrder; }

    mapResultToTableRow = (location: Location): Row => ({
        id:      location.id,
        name:    location.name,
        address: location.address || '—',
        actions: <LocationRowActions location={location} className={'Locations__delete'}/>,
        raw:     location,
    });

    getFilterSchema(): FilterSchema<Location> {
        return {
            name: {
                type: 'client',
                apply: (location: Location) => {
                    const needle = (this.filterValues.name as string)?.toLowerCase() || '';
                    return location.name?.toLowerCase().includes(needle)
                        || (location.address || '').toLowerCase().includes(needle);
                },
            },
        };
    }

    /** Convenience alias used across the app for location picker lists. */
    get locations(): Location[] { return this.allRows; }

    getName(id?: string | null): string | undefined {
        if (!id) return undefined;
        return this.allRows.find(l => l.id === id)?.name;
    }

    getById(id?: string | null): Location | undefined {
        if (!id) return undefined;
        return this.allRows.find(l => l.id === id);
    }

    /**
     * Manual optimistic update for callers that just ran a mutation. Subscriptions
     * keep this in sync on a live server, but the mock backend never fires them
     * (see resolversReliabilityAudit.ts), so action call sites must call this too.
     */
    upsertLocation(location: Location) {
        this._upsertItem(location, true);
    }

    removeLocation(id: string) {
        this._removeItem(id);
    }
}
